import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { MapPin, Award, Users, Target } from 'lucide-react';
import { motion } from 'framer-motion';

export default function About() {
  const values = [
    {
      icon: <Target className="h-8 w-8" />,
      title: 'Design Excellence',
      description: 'We pursue perfection in every detail, creating timeless spaces that inspire.'
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Client-Centric',
      description: 'Your vision guides our process. We listen, collaborate, and deliver beyond expectations.'
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: 'Quality Craftsmanship',
      description: 'Premium materials, skilled artisans, and meticulous attention to detail in every project.'
    }
  ];

  return (
    <div className="py-16 md:py-24" data-testid="about-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
            About Us
          </h2>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
            Crafting Spaces, Creating Stories
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            At Interio Mart, we believe that exceptional interior design goes beyond aesthetics—it's about creating environments that enhance lives and inspire joy.
          </p>
        </div>

        {/* Philosophy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Card className="overflow-hidden shadow-lg">
              <AspectRatio ratio={4/3}>
                <img
                  src="https://images.unsplash.com/photo-1705321963943-de94bb3f0dd3"
                  alt="Modern interior design"
                  className="w-full h-full object-cover"
                />
              </AspectRatio>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-semibold mb-6">Our Philosophy</h3>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              Founded with a passion for minimalist elegance and functional beauty, Interio Mart has been transforming spaces across Bengaluru and Mumbai. Our approach combines timeless design principles with contemporary sensibilities.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              We believe in the power of simplicity—clean lines, natural materials, and thoughtful spatial planning create environments that are both beautiful and livable. Every project is a collaboration, where your vision meets our expertise.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed">
              From residential havens to commercial landmarks, we craft spaces that tell stories and stand the test of time.
            </p>
          </motion.div>
        </div>

        {/* Values */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
              Our Values
            </h2>
            <h3 className="text-3xl md:text-4xl font-semibold">
              What Drives Us
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 text-center card-hover">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-accent rounded-full text-primary mb-4">
                    {value.icon}
                  </div>
                  <h4 className="text-xl font-semibold mb-3">{value.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{value.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Locations */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
              Where to Find Us
            </h2>
            <h3 className="text-3xl md:text-4xl font-semibold">
              Our Locations
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Bengaluru */}
            <Card className="p-8 card-hover" data-testid="location-bengaluru">
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-semibold mb-2">Bengaluru Studio</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Ground floor, Interio Mart, G-A, No 5AC, 905, Outer Ring Road, HRBR Layout, Babusapalya, Bengaluru, Karnataka 560043
                  </p>
                </div>
              </div>
              <div className="space-y-2 ml-9">
                <p className="text-sm">
                  <strong>Phone:</strong> <a href="tel:+918310951921" className="text-primary hover:underline">+91 8310951921</a>
                </p>
                <p className="text-sm">
                  <strong>Email:</strong> <a href="mailto:info@interiomart.co.in" className="text-primary hover:underline">info@interiomart.co.in</a>
                </p>
              </div>
            </Card>

            {/* Mumbai */}
            <Card className="p-8 card-hover" data-testid="location-mumbai">
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-semibold mb-2">Mumbai Studio</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Bharat compound, GM link Road, Goregaon East, Mumbai 400063
                  </p>
                </div>
              </div>
              <div className="space-y-2 ml-9">
                <p className="text-sm">
                  <strong>Phone:</strong> <a href="tel:+918310951921" className="text-primary hover:underline">+91 8310951921</a>
                </p>
                <p className="text-sm">
                  <strong>Email:</strong> <a href="mailto:info@interiomart.co.in" className="text-primary hover:underline">info@interiomart.co.in</a>
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Card className="p-8 md:p-12 bg-accent">
            <h3 className="text-3xl font-semibold mb-4">Let's Create Something Beautiful</h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Whether you're redesigning a single room or planning a complete transformation, we'd love to hear about your project.
            </p>
            <Link to="/contact">
              <Button size="lg" className="transition-colors duration-200" data-testid="about-cta-button">
                Get in Touch
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}